/**
 * Lista dos postos de trabalho avaliados da AET (#356): a faixa "Precisam de atenção" (contagens da
 * lista inteira), filtros em linha na URL, a situação de cada posto e os quatro vazios (empresa sem
 * setor, empresa sem posto, filtro sem resultado e "no seu acesso").
 *
 * Alvo visual: docs/superpowers/specs/2026-10-22-356-aet-mockup.html (aba 1).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

import {
  ControleIconButton,
  DataTable,
  DeleteWithDependenciesDialog,
  InlineFilterBar,
  InlineSelect,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components';
import { PrerequisiteBlock } from '../../components/common';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useAcao, useControle, type ControleDeAcesso } from '../../authorization/useVeredito';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';
import type { ActiveChip } from '../../hooks/useFilterState';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import {
  ergonomicWorkstationService,
  type ErgonomicSituation,
  type ErgonomicWorkstationDependencies,
  type ErgonomicWorkstationListFilters,
  type ErgonomicWorkstationListItem,
  type ErgonomicWorkstationSummary,
} from '../../services/ergonomicWorkstationService';
import { getApiErrorMessage, isApiError } from '../../types/common';
import { INACTIVE_LABEL, SITUATION_LABELS } from './situationLabels';
import { SituationChip } from './SituationChip';
import { MainText } from './MainText';

const PAGE_SIZE = 20;
const TOUCH_TARGET = 44;

/** "Inativo" não é uma situação calculada: na URL é um valor à parte, que a API recebe como `status`. */
const INACTIVE_FILTER = 'INATIVO';
type SituationFilter = ErgonomicSituation | typeof INACTIVE_FILTER;

const SITUATION_FILTER_OPTIONS: readonly { value: SituationFilter; label: string }[] = [
  { value: 'EM_CADASTRO', label: SITUATION_LABELS.EM_CADASTRO },
  { value: 'ACOES_EM_ABERTO', label: SITUATION_LABELS.ACOES_EM_ABERTO },
  { value: 'CONCLUIDO', label: SITUATION_LABELS.CONCLUIDO },
  { value: INACTIVE_FILTER, label: INACTIVE_LABEL },
];

type SelectOption = { value: string; label: string };

/** Id vindo da URL: só dígitos valem; qualquer outra coisa é como se não estivesse lá. */
const onlyDigits = (value: string | null): string => (value && /^\d+$/.test(value) ? value : '');

const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

/** As frases dos vínculos que bloqueiam a exclusão (o helper compartilhado não conhece estas chaves). */
function dependencyItems(details?: ErgonomicWorkstationDependencies['details']): string[] {
  if (!details) return [];
  const phrases: [number, string, string][] = [
    [details.activities, 'atividade', 'atividades'],
    [details.loads, 'carga levantada', 'cargas levantadas'],
    [details.equipments, 'equipamento', 'equipamentos'],
    [details.damages, 'dano à saúde', 'danos à saúde'],
    [details.actions, 'ação recomendada', 'ações recomendadas'],
    [details.photos, 'foto', 'fotos'],
  ];
  return phrases.filter(([count]) => count > 0).map(([count, one, many]) => plural(count, one, many));
}

/** Com acesso, o nome acessível diz a ação e o posto; sem acesso, fica o motivo que o veredito já traz. */
const namedControl = (controle: ControleDeAcesso, name: string): ControleDeAcesso =>
  controle.disabled ? controle : { ...controle, tooltip: name };

const SmallText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" color="text.secondary">
    {children}
  </Typography>
);

interface ColumnOptions {
  showCompany: boolean;
  riskLabels: ReadonlyMap<string, string>;
  actions: (workstation: ErgonomicWorkstationListItem) => React.ReactNode;
}

const buildColumns = ({
  showCompany,
  riskLabels,
  actions,
}: ColumnOptions): DataTableColumn<ErgonomicWorkstationListItem>[] => [
  {
    id: 'name',
    label: 'Posto de trabalho',
    primary: true,
    render: (workstation) => (
      <Box>
        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
          {workstation.name}
        </Typography>
        <SmallText>{`Processo: ${workstation.process}`}</SmallText>
      </Box>
    ),
  },
  {
    id: 'department',
    label: 'Setor',
    render: (workstation) => (
      <Box>
        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
          {workstation.departmentName ?? '—'}
        </Typography>
        {showCompany && workstation.companyName && <SmallText>{workstation.companyName}</SmallText>}
      </Box>
    ),
  },
  { id: 'exposed', label: 'Expostos', align: 'right', render: (workstation) => <MainText>{workstation.exposedCount}</MainText> },
  {
    id: 'risk',
    label: 'Nível de risco',
    render: (workstation) => (
      <MainText>
        {workstation.riskLevel ? riskLabels.get(workstation.riskLevel) ?? workstation.riskLevel : 'Não informado'}
      </MainText>
    ),
  },
  { id: 'activities', label: 'Atividades', align: 'right', render: (workstation) => <MainText>{workstation.activityCount}</MainText> },
  { id: 'photos', label: 'Fotos', align: 'right', render: (workstation) => <MainText>{workstation.photoCount}</MainText> },
  {
    id: 'openActions',
    label: 'Ações em aberto',
    align: 'right',
    render: (workstation) => <MainText>{workstation.openActionCount}</MainText>,
  },
  {
    id: 'situation',
    label: 'Situação',
    render: (workstation) => <SituationChip status={workstation.status} situation={workstation.situation} />,
  },
  { id: 'actions', label: 'Ações', align: 'center', render: actions },
];

export const ErgonomicWorkstationListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { shouldShowCompanyField } = useCompanyContext();
  const editar = useControle('/ergonomic-workstations/:id/edit', pode('ergonomic_workstation.update'), 'Editar');
  const excluir = useAcao('ergonomic_workstation.delete', 'Excluir');
  const criar = useControle(
    '/ergonomic-workstations/new',
    pode('ergonomic_workstation.create'),
    'Cadastrar o primeiro posto'
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<ErgonomicWorkstationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [summary, setSummary] = useState<ErgonomicWorkstationSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [riskLabels, setRiskLabels] = useState<ReadonlyMap<string, string>>(new Map());
  const [reload, setReload] = useState(0);

  const search = searchParams.get('search') ?? '';
  // Na URL a página conta de 1; aqui, de 0 (como o DataTable).
  const page = Math.max(Number(onlyDigits(searchParams.get('page')) || 1) - 1, 0);
  const companyId = shouldShowCompanyField ? onlyDigits(searchParams.get('companyId')) : '';
  const departmentId = onlyDigits(searchParams.get('departmentId'));
  const situationParam = searchParams.get('situation');
  const situation = SITUATION_FILTER_OPTIONS.find((option) => option.value === situationParam)?.value ?? '';
  const onlyWithoutPhoto = searchParams.get('semFoto') === 'true';

  const loadCompanyOptions = useCallback(async (): Promise<readonly SelectOption[]> => {
    const companies = await companyService.getCompaniesForDropdown();
    return companies.map((company) => ({ value: String(company.id), label: company.name }));
  }, []);
  const companyList = useListaPermitida('company.read', 'Empresas', loadCompanyOptions, {
    habilitado: shouldShowCompanyField,
  });

  const loadDepartmentOptions = useCallback(async (): Promise<readonly SelectOption[]> => {
    const response = await departmentService.getDepartments({
      page: 1,
      limit: 1000,
      ...(companyId ? { companyId } : {}),
    });
    return response.data.map((department) => ({ value: String(department.id), label: department.name }));
  }, [companyId]);
  const departmentList = useListaPermitida('department.read', 'Setores', loadDepartmentOptions);

  /** A página mora na URL junto dos filtros (de 0 aqui; a primeira não aparece na URL). */
  const goToPage = useCallback(
    (index: number) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (index > 0) next.set('page', String(index + 1));
          else next.delete('page');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Os rótulos do nível de risco vêm do servidor; sem eles a tela mostra o valor cru, que ainda é legível.
  useEffect(() => {
    let alive = true;
    ergonomicWorkstationService
      .options()
      .then((options) => alive && setRiskLabels(new Map(options.riskLevels.map((level) => [level.value, level.label]))))
      .catch(() => alive && setRiskLabels(new Map()));
    return () => {
      alive = false;
    };
  }, []);

  // A faixa conta a lista inteira da empresa e do setor escolhidos, não a página nem a situação filtrada.
  useEffect(() => {
    let alive = true;
    const scope: ErgonomicWorkstationListFilters = {};
    if (companyId) scope.companyId = Number(companyId);
    if (departmentId) scope.departmentId = Number(departmentId);
    setSummaryError(false);
    ergonomicWorkstationService
      .summary(scope)
      .then((response) => alive && setSummary(response))
      .catch(() => {
        if (!alive) return;
        setSummary(null);
        setSummaryError(true);
      });
    return () => {
      alive = false;
    };
  }, [companyId, departmentId, reload]);

  useEffect(() => {
    let alive = true;
    let redirected = false;
    const filters: ErgonomicWorkstationListFilters = {};
    if (companyId) filters.companyId = Number(companyId);
    if (departmentId) filters.departmentId = Number(departmentId);
    if (search) filters.search = search;
    if (situation === INACTIVE_FILTER) filters.status = 'INACTIVE';
    else if (situation) filters.situation = situation;
    if (onlyWithoutPhoto) filters.semFoto = true;

    setLoading(true);
    setError(null);
    setForbidden(false);
    ergonomicWorkstationService
      .list({ ...filters, page: page + 1, limit: PAGE_SIZE })
      .then((response) => {
        if (!alive) return;
        // Excluiu o último posto da última página: a página sumiu, mas a lista não está vazia.
        if (response.workstations.length === 0 && page > 0) {
          redirected = true;
          const lastPage = Math.max(Math.ceil(response.total / PAGE_SIZE) - 1, 0);
          goToPage(Math.min(page - 1, lastPage));
          return;
        }
        setRows(response.workstations);
        setTotal(response.total);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setRows([]);
        setTotal(0);
        if (isApiError(err) && err.status === 403) setForbidden(true);
        else setError(getApiErrorMessage(err, 'Erro ao carregar os postos de trabalho'));
      })
      .finally(() => alive && !redirected && setLoading(false));
    return () => {
      alive = false;
    };
  }, [page, companyId, departmentId, search, situation, onlyWithoutPhoto, reload, goToPage]);

  /** Grava filtros na URL (vazio apaga a chave) e volta à primeira página. */
  const setUrlFilters = useCallback(
    (changes: Partial<Record<'search' | 'companyId' | 'departmentId' | 'situation' | 'semFoto', string>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          next.delete('page');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // A empresa é o escopo da lista e não um recorte dela: limpar filtros mantém a empresa escolhida.
  const clearFilters = () => setUrlFilters({ search: '', departmentId: '', situation: '', semFoto: '' });
  const retry = () => setReload((count) => count + 1);

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<ErgonomicWorkstationListItem, ErgonomicWorkstationDependencies>({
      checkDependencies: (id) => ergonomicWorkstationService.dependencies(id),
      deleteEntity: async (id) => {
        await ergonomicWorkstationService.remove(id);
        enqueueSnackbar('Posto excluído.', { variant: 'success' });
      },
      checkErrorMessage: 'Erro ao verificar o que está ligado ao posto',
      deleteErrorMessage: 'Erro ao excluir o posto',
      onError: setActionError,
      onDeleted: retry,
    });

  // Ícones de 44px: o `actions` do DataTable é comum e fixa 30px, então a coluna de ações é daqui.
  const columns = useMemo(
    () =>
      buildColumns({
        showCompany: shouldShowCompanyField,
        riskLabels,
        actions: (workstation) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <ControleIconButton
              controle={namedControl(editar, `Editar posto ${workstation.name}`)}
              onClick={() => navigate(`/ergonomic-workstations/${workstation.id}/edit`)}
              sx={{ width: TOUCH_TARGET, height: TOUCH_TARGET }}
            >
              <EditIcon />
            </ControleIconButton>
            <ControleIconButton
              controle={namedControl(excluir, `Excluir posto ${workstation.name}`)}
              color="error"
              onClick={() => handleDeleteClick(workstation)}
              sx={{ width: TOUCH_TARGET, height: TOUCH_TARGET }}
            >
              <DeleteIcon />
            </ControleIconButton>
          </Box>
        ),
      }),
    [shouldShowCompanyField, riskLabels, editar, excluir, navigate, handleDeleteClick]
  );

  const optionLabel = (options: readonly SelectOption[], value: string) =>
    options.find((option) => option.value === value)?.label ?? value;

  const chips: ActiveChip[] = [];
  if (companyId) {
    chips.push({
      key: 'companyId',
      label: 'Empresa',
      value: optionLabel(companyList.itens, companyId),
      onDelete: () => setUrlFilters({ companyId: '', departmentId: '' }),
    });
  }
  if (departmentId) {
    chips.push({
      key: 'departmentId',
      label: 'Setor',
      value: optionLabel(departmentList.itens, departmentId),
      onDelete: () => setUrlFilters({ departmentId: '' }),
    });
  }
  if (situation) {
    chips.push({
      key: 'situation',
      label: 'Situação',
      value: optionLabel(SITUATION_FILTER_OPTIONS, situation),
      onDelete: () => setUrlFilters({ situation: '' }),
    });
  }
  if (onlyWithoutPhoto) {
    chips.push({
      key: 'semFoto',
      label: 'Fotos',
      value: 'sem foto',
      onDelete: () => setUrlFilters({ semFoto: '' }),
    });
  }

  const filtersActive = Boolean(search || departmentId || situation || onlyWithoutPhoto);
  // Quem escolhe a empresa, ou só tem uma, olha uma empresa; quem tem várias e não escolheu olha uma fatia.
  const scopeIsCompany = !shouldShowCompanyField || companyId !== '';
  const hasNoSector =
    !loading &&
    !error &&
    !forbidden &&
    total === 0 &&
    !filtersActive &&
    scopeIsCompany &&
    departmentList.permitido &&
    departmentList.resolvida &&
    !departmentList.erro &&
    departmentList.itens.length === 0;

  const toggleSituation = (value: SituationFilter) => setUrlFilters({ situation: situation === value ? '' : value });
  const toggleWithoutPhoto = () => setUrlFilters({ semFoto: onlyWithoutPhoto ? '' : 'true' });
  const attention: { value: SituationFilter; label: string; count: number }[] = summary
    ? [
        { value: 'EM_CADASTRO', label: 'em cadastro', count: summary.emCadastro },
        {
          value: 'ACOES_EM_ABERTO',
          label: summary.acoesEmAberto === 1 ? 'com ação em aberto' : 'com ações em aberto',
          count: summary.acoesEmAberto,
        },
      ]
    : [];
  const withAttention = attention.filter((item) => item.count > 0);

  // Um objeto com texto literal por situação: o teste dos textos de vazio lê cada description como
  // texto inteiro, então a escolha entre as frases fica no ternário de fora.
  const emptyState = scopeIsCompany
    ? {
        title: 'Nenhum posto avaliado nesta empresa',
        description:
          'Um posto é o lugar onde alguém faz uma tarefa, como a bancada de montagem. Crie o primeiro: você descreve as atividades, o levantamento de cargas, as ações recomendadas e anexa as fotos.',
        action: {
          label: 'Cadastrar o primeiro posto',
          node: 'ergonomic-workstations',
          deniedHint: 'Peça a um administrador para cadastrar os postos de trabalho desta empresa.',
          onClick: () => navigate('/ergonomic-workstations/new'),
          allowed: !criar.disabled,
        },
      }
    : {
        title: 'Nenhum posto avaliado no seu acesso',
        description:
          'Você vê os postos de mais de uma empresa, e nenhuma delas tem posto avaliado ainda. Escolha a empresa no filtro e cadastre o primeiro posto dela.',
        action: {
          label: 'Cadastrar o primeiro posto',
          node: 'ergonomic-workstations',
          deniedHint: 'Peça a um administrador para cadastrar os postos de trabalho do seu acesso.',
          onClick: () => navigate('/ergonomic-workstations/new'),
          allowed: !criar.disabled,
        },
      };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Análise ergonômica' }]}
      title="Análise ergonômica"
      subtitle="Cada posto de trabalho avaliado, com atividades, riscos, ações e fotos."
      actions={
        <RouteActionButton
          para="/ergonomic-workstations/new"
          exige={pode('ergonomic_workstation.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Novo posto
        </RouteActionButton>
      }
    >
      {forbidden && (
        <Alert severity="warning" sx={{ fontSize: '1rem', mb: 3 }}>
          Seu perfil não inclui a consulta dos postos de trabalho. Peça a um administrador para liberar o acesso.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ fontSize: '1rem', mb: 3 }}
          action={
            <Button color="inherit" onClick={retry} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ fontSize: '1rem', mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {!error && !forbidden && summaryError && (
        <Alert
          severity="warning"
          sx={{ fontSize: '1rem', mb: 3 }}
          action={
            <Button color="inherit" onClick={retry} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar a faixa "Precisam de atenção". A lista abaixo continua valendo.
        </Alert>
      )}

      {!error && !forbidden && !summaryError && summary === null && (
        <Skeleton variant="rounded" height={TOUCH_TARGET} sx={{ mb: 3 }} />
      )}

      {!error && !forbidden && summary !== null && summary.total > 0 && (
        <Box
          role="group"
          aria-labelledby="faixa-atencao"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}
        >
          <Typography id="faixa-atencao" variant="body1" color="text.secondary" sx={{ mr: 1 }}>
            Precisam de atenção
          </Typography>
          {withAttention.map((item) => (
            <Button
              key={item.value}
              variant={situation === item.value ? 'contained' : 'outlined'}
              color="inherit"
              aria-pressed={situation === item.value}
              onClick={() => toggleSituation(item.value)}
              sx={{ minHeight: TOUCH_TARGET, fontWeight: 600 }}
            >
              {`${item.count} ${item.label}`}
            </Button>
          ))}
          {(summary.semFoto > 0 || onlyWithoutPhoto) && (
            <Button
              variant={onlyWithoutPhoto ? 'contained' : 'outlined'}
              color="inherit"
              aria-pressed={onlyWithoutPhoto}
              onClick={toggleWithoutPhoto}
              sx={{ minHeight: TOUCH_TARGET, fontWeight: 600 }}
            >
              {`${summary.semFoto} sem foto`}
            </Button>
          )}
          {withAttention.length === 0 && summary.semFoto === 0 && !onlyWithoutPhoto && (
            <Typography variant="body1">Nenhum posto precisa de atenção agora.</Typography>
          )}
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <InlineFilterBar
          searchPlaceholder="Nome do posto ou processo"
          searchValue={search}
          onSearchChange={(value) => setUrlFilters({ search: value })}
          resultCount={error || forbidden ? undefined : total}
          resultNoun="postos"
          chips={chips}
          onClearAll={clearFilters}
          loading={loading}
        >
          {shouldShowCompanyField && (
            <Tooltip title={companyList.bloqueio ?? ''}>
              <span>
                <InlineSelect
                  label="Empresa"
                  value={companyId}
                  options={[...companyList.itens]}
                  onChange={(value) => setUrlFilters({ companyId: value, departmentId: '' })}
                  emptyLabel="Todas as empresas"
                  minWidth={200}
                  disabled={!companyList.permitido}
                />
              </span>
            </Tooltip>
          )}
          <Tooltip title={departmentList.bloqueio ?? ''}>
            <span>
              <InlineSelect
                label="Setor"
                value={departmentId}
                options={[...departmentList.itens]}
                onChange={(value) => setUrlFilters({ departmentId: value })}
                emptyLabel="Todos"
                minWidth={180}
                disabled={!departmentList.permitido}
              />
            </span>
          </Tooltip>
          <InlineSelect
            label="Situação"
            value={situation}
            options={[...SITUATION_FILTER_OPTIONS]}
            onChange={(value) => setUrlFilters({ situation: value })}
            emptyLabel="Todas"
            minWidth={200}
          />
        </InlineFilterBar>
      </Box>

      {hasNoSector && (
        <PrerequisiteBlock
          title="Esta empresa ainda não tem setores"
          reason="Todo posto de trabalho fica dentro de um setor. Cadastre o setor onde o trabalho acontece e volte aqui para criar o primeiro posto."
          resolve={{
            label: 'Cadastrar um setor',
            permission: 'department.create',
            mode: 'navigate',
            route: '/departments/create',
          }}
          deniedHint="Peça a quem administra a empresa para cadastrar um setor."
        />
      )}

      {!error && !forbidden && !hasNoSector && (
        <DataTable
          columns={columns}
          rows={rows}
          getRowId={(workstation) => workstation.id}
          loading={loading}
          filtersActive={filtersActive}
          onClearFilters={clearFilters}
          emptyState={emptyState}
          pagination={{
            page,
            rowsPerPage: PAGE_SIZE,
            totalCount: total,
            onPageChange: goToPage,
            onRowsPerPageChange: () => undefined,
            rowsPerPageOptions: [PAGE_SIZE],
          }}
        />
      )}

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasDependencies)}
        title="Excluir posto"
        confirmText="Tem certeza que deseja excluir este posto?"
        prerequisite={{
          title: 'Este posto já tem informações',
          reason: 'Um posto com atividades, cargas, equipamentos, danos, ações ou fotos não pode ser excluído. Remova esses itens primeiro.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Abrir o posto',
            permission: 'ergonomic_workstation.update',
            mode: 'navigate',
            route: `/ergonomic-workstations/${deleteTarget?.id}/edit`,
          },
          deniedHint: 'Peça a um administrador para remover o que está ligado a este posto antes de excluí-lo.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default ErgonomicWorkstationListPage;
